export type HomeCarePlan = {
  id: "essential" | "complete" | "premium";
  name: string;
  price: number;
  tagline: string;
  popular?: boolean;
  includes: string[];
  visits: string;
};

export const HOMECARE_PLANS: HomeCarePlan[] = [
  {
    id: "essential",
    name: "Essential",
    price: 65,
    tagline: "The basics, handled on schedule.",
    visits: "1 house wash · 2 gutter maintenance services / yr",
    includes: [
      "1 professional house wash per year",
      "2 annual gutter maintenance services per year",
      "Annual roof & gutter visual check — we text you photos if we spot anything",
      "Priority member scheduling",
      "Automatic seasonal service reminders",
      "15% off eligible additional services",
    ],
  },
  {
    id: "complete",
    name: "Complete",
    price: 80,
    tagline: "Our most popular year-round package.",
    popular: true,
    visits: "Essential + 1 exterior window cleaning / yr",
    includes: [
      "Everything in Essential",
      "1 professional exterior window cleaning per year (all exterior windows)",
      "Completed with or immediately after your annual house wash",
      "Priority member scheduling",
      "15% off eligible additional services",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    price: 99,
    tagline: "Total exterior care with nothing left to you.",
    visits: "Complete + a 2nd window cleaning / yr",
    includes: [
      "Everything in Complete",
      "A 2nd professional exterior window cleaning per year",
      "First cleaning with or after the house wash; second spaced later in the season",
      "Priority member scheduling",
      "15% off eligible additional services",
    ],
  },
];

export const HOMECARE_COMPARE: Array<{
  label: string;
  essential: boolean | string;
  complete: boolean | string;
  premium: boolean | string;
}> = [
  { label: "Professional house wash", essential: "1x / year", complete: "1x / year", premium: "1x / year" },
  { label: "Gutter maintenance services", essential: "2x / year", complete: "2x / year", premium: "2x / year" },
  { label: "Roof & gutter visual check (photos if flagged)", essential: "1x / year", complete: "1x / year", premium: "1x / year" },
  { label: "Exterior window cleaning (all windows)", essential: false, complete: "1x / year", premium: "2x / year" },
  { label: "Priority member scheduling", essential: true, complete: true, premium: true },
  { label: "Automatic seasonal reminders", essential: true, complete: true, premium: true },
  { label: "15% off eligible add-on services", essential: "15%", complete: "15%", premium: "15%" },
];

export const HOMECARE_ADDONS = [
  { name: "Soft-wash roof treatment", typical: 675, note: "1,800 sqft ranch" },
  { name: "Deck & fence cleaning", typical: 450, note: "Deck + 80 ft of fence" },
  { name: "Paver patio clean & seal", typical: 1200, note: "400 sqft patio" },
];

export const HOMECARE_FAQS = [
  {
    q: "How does Capital Pro Home Care billing work?",
    a: "You're billed a flat monthly rate — $65, $80 or $99 depending on the plan — on the same day each month. There's no big lump-sum invoice when we show up, and no surprise add-ons without your approval.",
  },
  {
    q: "Am I locked into a contract?",
    a: "Membership runs month to month with a 12-month service cycle so we can schedule your visits. You can cancel any time with 30 days' notice. If any work has already been performed before you cancel, full payment for the work completed is due at standard one-off pricing.",
  },
  {
    q: "What happens in the winter?",
    a: "Exterior cleaning in the Capital Region runs roughly April through November. Your monthly payment spreads that work evenly across the year instead of hitting you all at once in the spring, and winter months are when we handle scheduling and any storm-related follow-ups.",
  },
  {
    q: "Do I have to be home for the visits?",
    a: "No. As long as we have access to an outdoor spigot and to the areas being cleaned, most members never take time off work. You get a text the day before and a text when we're done — with photos if we spotted anything worth flagging.",
  },
  {
    q: "How do I reschedule a visit?",
    a: "Call or text (518) 900-1913 and we'll move it. Weather delays are on us — we reschedule automatically and let you know the new date.",
  },
  {
    q: "Can I stack the member discount with other offers?",
    a: "The 15% member discount applies to every add-on service and beats our public promotions, so it doesn't stack with other coupons or seasonal specials. You always get whichever price is lower.",
  },
  {
    q: "Which towns are covered?",
    a: "Home Care is available anywhere we service across New York's Capital Region — Albany, Schenectady, Troy, Saratoga Springs, Clifton Park, Colonie, Latham, Guilderland, Delmar, Niskayuna, Rensselaer, Ballston Spa and the surrounding towns.",
  },
  {
    q: "Is a membership actually cheaper than booking one-off?",
    a: "A house wash, two gutter maintenance services and an exterior window cleaning typically run about $999 a year at one-off pricing. The Complete plan covers that same work for $80 a month ($960 a year) — actually less than the one-off cost — and adds priority scheduling, seasonal reminders, a free roof & gutter visual check on every wash, and 15% off everything else.",
  },
  {
    q: "Who actually does the work?",
    a: "Our own trained Capital Pro crew — never subcontractors. Same techs, same equipment, same soft-wash process we've used for 10 years in the Capital Region.",
  },
  {
    q: "Are you licensed and insured?",
    a: "Yes. Capital Pro Pressure Washing is fully licensed and insured, and we carry general liability coverage on every job, membership or not.",
  },
];

export const HOMECARE_TERMS = [
  "Plan pricing is based on homes up to 2,500 sqft and up to 200 linear feet of gutter. Larger properties are quoted individually at member rates.",
  "Monthly membership is billed automatically to the card on file on the same day each month. A 12-month service cycle applies so visits can be scheduled.",
  "Cancel any time with 30 days' written notice. If any work has been performed, full payment for the work completed is due at standard one-off pricing.",
  "Scheduled visits are weather dependent and are rescheduled at no charge.",
  "The 15% member discount applies to add-on services only and cannot be combined with other promotions, coupons or seasonal specials.",
  "Membership is tied to the service address and is not transferable without written approval.",
  "The annual roof and gutter check is a casual visual check performed from the ground or ladder-safe zones during a scheduled wash. It is not a professional roof inspection, home inspection, or leak-detection service, and it does not cover areas not visible from ground level or a ladder. This is not a substitute for a professional roof inspection.",
  "Photos from each visit are sent via text or email when the crew notices something worth flagging. A formal written report is not produced on every visit unless a concern is identified.",
  "Annual gutter maintenance services include a standard gutter cleanout or debris removal from accessible gutter guards. Removal and reinstallation of gutter guards or specialty gutter systems may require an additional charge based on your home's gutter system.",
];
